import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { format, utcToZonedTime } from 'date-fns-tz';
import type { FC } from 'react';

import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { CategoryIdE } from 'declarations/enums/releaseNotes';
import { StorageE } from 'declarations/enums/storage';
import type { LatestReleaseNoteI } from 'declarations/interfaces/releaseNotes';

import { getPopupContentStyle } from './ReleaseNotePopup.styled';

type ReleaseNotePopupT = {
  displayNotes: boolean;
  latestReleaseNote: LatestReleaseNoteI;
};

const ReleaseNotePopup: FC<ReleaseNotePopupT> = ({ displayNotes, latestReleaseNote }) => {
  const { isMobile } = useResponsive();

  return (
    <>
      {displayNotes && (
        <Popup isOpen={true} contentStyle={getPopupContentStyle(isMobile)}>
          <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
            <Typography variant="h6" color="text.primary">
              {format(utcToZonedTime(latestReleaseNote.releaseDate, 'UTC'), 'MMM do, yyyy', {
                timeZone: 'UTC',
              })}
            </Typography>
            {latestReleaseNote.version && (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ ml: 0.625, p: 0.25, bgcolor: 'grey.200', borderRadius: 1 }}
              >
                v{latestReleaseNote.version}
              </Typography>
            )}
          </Box>
          <Typography variant="subtitle1" color="text.primary" sx={{ mt: 1 }}>
            {latestReleaseNote.categoryId === CategoryIdE.WhatsNew ? "What's new" : 'Fixed issues'}
          </Typography>
          <List sx={{ listStyle: 'disc', listStylePosition: 'inside', mx: 1.25 }}>
            <ListItem
              sx={{
                display: 'list-item',
                py: 0.5,
                px: 0.5,
                my: 0.5,
                bgcolor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1">{latestReleaseNote.note}</Typography>
              {latestReleaseNote.description && (
                <Typography variant="body2" color="text.secondary">
                  {latestReleaseNote.description}
                </Typography>
              )}
            </ListItem>
          </List>
          <Button
            variant="outlined"
            onClick={(): void => {
              localStorage.setItem(StorageE.IsLatestReleaseNoteDismissed, 'true');
              window.location.reload();
            }}
          >
            Dismiss
          </Button>
        </Popup>
      )}
    </>
  );
};

export default ReleaseNotePopup;
